import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api.js';
import { useAuth } from '../context/AuthContext.jsx';

export default function ClaimPlayer() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [error, setError] = useState(null);
  const [busyName, setBusyName] = useState(null);

  const load = () => {
    setError(null);
    api
      .joinGame(code)
      .then((g) => {
        const game = g?.game || g;
        const list = (game?.teams || []).flatMap((t) =>
          (t.players || []).map((p) => ({ ...p, team: t.name }))
        );
        setPlayers(list);
      })
      .catch((err) => setError(err.message));
  };

  useEffect(load, [code]);

  const pick = async (p) => {
    if (busyName) return;
    // Taken by someone else? Ignore the tap.
    if (p.userId && p.userId !== user?.uid) return;

    // Guests: local-only claim, no server write (stats won't track).
    if (!user?.uid || user?.isGuest) {
      localStorage.setItem(
        `pubgolf.player.${code}`,
        JSON.stringify({ name: p.name, team: p.team, guest: true })
      );
      navigate(`/game/${code}`);
      return;
    }

    // Real account: hit the server so stats track on finish.
    setBusyName(p.name);
    try {
      await api.claimPlayer(code, {
        playerName: p.name,
        userId: user.uid,
        displayName: user.displayName,
        avatar: user.avatar,
      });
      localStorage.setItem(
        `pubgolf.player.${code}`,
        JSON.stringify({
          name: p.name,
          team: p.team,
          userId: user.uid,
        })
      );
      navigate(`/game/${code}`);
    } catch (err) {
      if (err.status === 409) {
        setError(
          `${p.name} is already claimed by someone else. Pick another slot.`
        );
        load(); // refresh so we see the other claim
      } else {
        setError(err.message || 'Could not claim that slot.');
      }
    } finally {
      setBusyName(null);
    }
  };

  return (
    <div className="screen">
      <div className="screen-header">
        <button className="back-btn" onClick={() => navigate(-1)}>←</button>
        <h1>Claim Player</h1>
        <div className="spacer" />
      </div>

      <div className="text-muted" style={{ marginBottom: '1.25rem' }}>
        Tap your name to join the scorecard.{' '}
        {user?.isGuest && (
          <span className="text-dim">
            You're playing as a guest — stats won't be tracked.
          </span>
        )}
      </div>

      {error && (
        <div className="card" style={{ color: 'var(--danger)', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      <div className="stack-sm">
        {players.map((p, i) => {
          const takenByOther = !!p.userId && p.userId !== user?.uid;
          const takenByYou = !!p.userId && p.userId === user?.uid;
          return (
            <button
              key={p.id || i}
              className="friend-row"
              style={{
                margin: 0,
                width: '100%',
                opacity: takenByOther ? 0.55 : 1,
              }}
              disabled={takenByOther || busyName === p.name}
              onClick={() => pick(p)}
            >
              <div className="avatar">
                {p.avatar || p.name?.[0]?.toUpperCase()}
              </div>
              <div className="grow">
                <div className="name" style={{ textAlign: 'left' }}>
                  {p.name}{' '}
                  {takenByYou && (
                    <span className="chip chip-accent" style={{ marginLeft: '0.4rem' }}>
                      ✓ You
                    </span>
                  )}
                  {takenByOther && (
                    <span className="chip" style={{ marginLeft: '0.4rem' }}>
                      Taken
                    </span>
                  )}
                </div>
                <div className="sub" style={{ textAlign: 'left' }}>
                  {p.team}
                </div>
              </div>
              {busyName === p.name ? (
                <div className="spinner" style={{ width: 18, height: 18 }} />
              ) : (
                <div style={{ color: 'var(--text20)' }}>›</div>
              )}
            </button>
          );
        })}
      </div>

      {players.length === 0 && !error && (
        <div className="empty-state">
          <div className="spinner" style={{ margin: '0 auto' }} />
        </div>
      )}
    </div>
  );
}
